import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  RemarkRow,
  buildRemarksRows,
  fetchApproverDetails,
  findOneLeaveEncashment,
  displayEmpId,
  getEmployeeDisplayName,
  getLoggedInEmpId,
  getRequestStatusUi,
  padEmpId,
  parseSapDate,
} from "./leaveEncashment.service";

const NO_DATA = "-";

const LeaveEncashmentDetail: React.FC = () => {
  const { t } = useT();
  const routes = all_routes;
  const location = useLocation();
  const [params] = useSearchParams();
  const type = params.get("type") || (location.state as any)?.type || "compensation";
  const routeItem = (location.state as any)?.item;
  const refFromUrl = params.get("ref") || "";
  const label = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  if (type === "quota") {
    return <QuotaDetail item={routeItem} label={label} listPath={routes.leaveEncashment.list} />;
  }
  return (
    <CompensationDetail
      routeItem={routeItem}
      referenceFromUrl={refFromUrl}
      label={label}
      listPath={routes.leaveEncashment.list}
      t={t}
    />
  );
};

const QuotaDetail: React.FC<{
  item: any;
  label: (key: string, fallback: string) => string;
  listPath: string;
}> = ({ item, label, listPath }) => (
  <div className="page-wrapper">
    <div className="content">
      <PageHeader title={label("leave_quota_details", "Leave Quota Details")} listPath={listPath} listLabel={label("leave_encashment_overview", "Leave Encashment Overview")} />
      {!item ? (
        <Empty text={label("no_details_available", "No details available")} listPath={listPath} />
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <InfoRow label={label("employee_id", "Employee ID")} value={displayEmpId(item, padEmpId()) || undefined} />
            <InfoRow label={label("leave_type", "Leave Type")} value={item.QuotaText} />
            <InfoRow label={label("quota_type", "Quota Type")} value={item.QuotaType} />
            <InfoRow label={label("deductible_from", "Deductible From")} value={parseSapDate(item.DeductionFrom)} />
            <InfoRow label={label("deductible_to", "Deductible To")} value={parseSapDate(item.DeductionTo)} />
            <InfoRow label={label("entitlement", "Entitlement")} value={item.QuotaNumber} />
            <InfoRow label={label("remainder", "Remainder")} value={item.RemQuota} />
          </div>
        </div>
      )}
    </div>
  </div>
);

const CompensationDetail: React.FC<{
  routeItem: any;
  referenceFromUrl: string;
  label: (key: string, fallback: string) => string;
  listPath: string;
  t: (key: string) => string;
}> = ({ routeItem, referenceFromUrl, label, listPath, t }) => {
  const [detail, setDetail] = useState<any>(null);
  const [approvers, setApprovers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const referenceNumber = routeItem?.ReferenceNumber || referenceFromUrl || "";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (referenceNumber) {
          const data = await findOneLeaveEncashment(referenceNumber);
          setDetail(data ? { Status: routeItem?.Status, ...data } : routeItem ?? null);
          const empId = displayEmpId(routeItem, displayEmpId(data, padEmpId()));
          setApprovers(await fetchApproverDetails(empId, referenceNumber));
        } else {
          setDetail(routeItem ?? null);
        }
      } catch (err: any) {
        setDetail(routeItem ?? null);
        toast.error(Api.getErrorMessage(err) || "Failed to load details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [referenceNumber, routeItem]);

  const statusUi = getRequestStatusUi(detail?.RequestStatus || "", t);
  const rows = buildRemarksRows(approvers, t, {
    personnelNumber: getLoggedInEmpId(),
    name: getEmployeeDisplayName(),
  });

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title={label("leave_encashment_details", "Leave Encashment Details")} listPath={listPath} listLabel={label("leave_encashment_overview", "Leave Encashment Overview")} />
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>
        ) : !detail ? (
          <Empty text={label("no_details_available", "No details available")} listPath={listPath} />
        ) : (
          <>
            <div className="alert alert-light d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{label("request_status", "Request Status")}</span>
              <span className={statusUi.badge}>{statusUi.label}</span>
            </div>
            <Section title={label("request_information", "Request Information")}>
              <InfoRow label={label("reference_no", "Reference No")} value={detail.ReferenceNumber} />
              <InfoRow label={label("employee_id", "Employee ID")} value={displayEmpId(detail, displayEmpId(routeItem, padEmpId())) || undefined} />
            </Section>
            <Section title={label("leave_details", "Leave Details")}>
              <InfoRow label={label("leave_type", "Leave Type")} value={detail.QuotaText} />
              <InfoRow label={label("leave_encashment_method", "Leave Encashment Method")} value={detail.Description} />
              <InfoRow label={t("days_to_be_encashed")} value={detail.QuotaNumber} />
              <InfoRow label={label("quota_type", "Quota Type")} value={detail.QuotaText} />
              {!!detail.OffCycleIndicator && <InfoRow label={t("off_cycle_indicator")} value={detail.OffCycleIndicator} />}
            </Section>
            <Section title={t("remarks")}>
              <RemarksCards
                referenceNumber={detail.ReferenceNumber || referenceNumber}
                rows={rows}
                emptyText={label("no_data_available", "No data available")}
                referenceLabel={label("reference_no", "Reference No")}
                nameLabel={label("name", "Name")}
                remarkLabel={t("remarks")}
              />
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

export const RemarksCards: React.FC<{
  referenceNumber: string;
  rows: RemarkRow[];
  emptyText: string;
  referenceLabel: string;
  nameLabel: string;
  remarkLabel: string;
}> = ({ referenceNumber, rows, emptyText, referenceLabel, nameLabel, remarkLabel }) => {
  if (!rows.length) return <div className="text-muted">{emptyText}</div>;
  return (
    <div className="d-flex flex-column gap-2">
      {rows.map((row, index) => (
        <div className="border rounded p-3" key={`${row.role}-${index}`}>
          <div className="fw-semibold text-danger mb-2">{row.role}: {row.personnelNumber || NO_DATA}</div>
          <InfoRow label={referenceLabel} value={referenceNumber || NO_DATA} />
          <InfoRow label={nameLabel} value={row.name || NO_DATA} />
          <InfoRow label={remarkLabel} value={row.remark || NO_DATA} />
        </div>
      ))}
    </div>
  );
};

const PageHeader: React.FC<{ title: string; listPath: string; listLabel: string }> = ({ title, listPath, listLabel }) => {
  const navigate = useNavigate();
  const { t } = useT();
  return (
    <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
      <div>
        <h3 className="page-title mb-1">{title}</h3>
        <nav>
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><Link to={listPath}>{listLabel}</Link></li>
            <li className="breadcrumb-item active" aria-current="page">{title}</li>
          </ol>
        </nav>
      </div>
      <button type="button" className="btn btn-light" onClick={() => navigate(-1)}>
        <i className="ti ti-arrow-left me-1"></i>
        {t("back")}
      </button>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white fw-bold">{title}</div>
    <div className="card-body">{children}</div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? NO_DATA}</span>
  </div>
);

const Empty: React.FC<{ text: string; listPath: string }> = ({ text }) => {
  const navigate = useNavigate();
  const { t } = useT();
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body text-center py-5">
        <p className="text-muted">{text}</p>
        <button type="button" className="btn btn-light" onClick={() => navigate(-1)}>
          <i className="ti ti-arrow-left me-1"></i>
          {t("back")}
        </button>
      </div>
    </div>
  );
};

export default LeaveEncashmentDetail;
